// Flipbook preview assets — rasterize the FINAL print PDFs into web-optimized
// page JPEGs + a manifest, and upload them to the private `previews` bucket.
//
// Why from the PDFs and not from the editor DOM: the preview the client
// confirms must be pixel-identical to what prints (render-parity rule). The
// PDFs are born in a browser anyway (customer submit or admin regen — see
// multiAlbumPdf.renderAndUploadAlbumPdfs), so we rasterize them right there
// with pdfjs-dist (already a dependency) — "rendered once at confirm time",
// zero server CPU, and a regen automatically refreshes the preview.
//
// Output layout in the bucket (uniform, no album-1 legacy flat path):
//   orders/{shopifyOrderId}/album-{n}/page-{p}.jpg       (~1400px long edge, q0.8)
//   orders/{shopifyOrderId}/album-{n}/cover-front.jpg    (cropped from the A3+ wrap)
//   orders/{shopifyOrderId}/album-{n}/cover-spine.jpg
//   orders/{shopifyOrderId}/album-{n}/cover-back.jpg
//   orders/{shopifyOrderId}/album-{n}/manifest.json      (uploaded LAST = ready marker;
//                                                         page list + inline LQIP data-URIs)
//
// pdfjs is imported dynamically so this whole pipeline stays out of the main
// editor bundle until a confirm actually happens.

import { previewAssetsApi, uploadToSignedUrl } from '@/lib/api';
import type { PreviewRenderKeys } from './previewRenderKeys';
import { couponPagesFromKeywords, previewSourcePage } from './couponLeaves';
import {
  computeTemplateLayout,
  DEFAULT_PRINT_SETTINGS,
  withDerivedVinyl,
  type PrintSettings,
} from './assemblyTemplate';

// ─── Tunables ─────────────────────────────────────────────────────────────────
const PAGE_LONG_EDGE_PX = 1400;  // sharp tier — crisp on a phone, ~150-250 KB
const PAGE_JPEG_Q = 0.8;
const LQIP_WIDTH_PX = 24;        // blurry inline placeholder (~1 KB data URI)
const LQIP_JPEG_Q = 0.45;
const UPLOAD_CHUNK = 40;         // worker caps upload-url minting at 40/call

export interface PreviewManifest {
  version: 1;
  generated_at: string;
  page_count: number;
  /** Interior page width / height. The client page NEVER mentions paper sizes —
   *  aspect ratio is all the flipbook needs. */
  page_aspect: number;
  cover: Partial<Record<'front' | 'spine' | 'back', { file: string; aspect: number; lqip: string }>>;
  pages: Array<{ n: number; file: string; lqip: string }>;
  /** Pages that would not rasterize and were replaced by a placeholder. Absent
   *  or empty on a clean render. */
  failed_pages?: number[];
  /** What these pages were drawn from, per page — lets a quick preview redraw
   *  only what changed (lib/previewRenderKeys.ts). Absent = redraw everything. */
  render_keys?: PreviewRenderKeys;
  /** Drawn by the quick preview (preview-size photos) — the print rebuild
   *  replaces it within minutes. Diagnostic only. */
  quick?: boolean;
}

export interface PreviewAssetFile {
  name: string;
  blob: Blob;
}

export interface PreviewProgress {
  current: number;
  total: number;
  message: string;
}

// ─── Pure helpers (unit-tested) ───────────────────────────────────────────────

/** Crop windows for the three cover faces as FRACTIONS of the full A3+ wrap,
 *  derived from the same computeTemplateLayout the cover PDF + fiche use, so
 *  the flipbook's cover textures can never drift from print geometry.
 *  x grows rightward, y downward (canvas space; the layout's pt origin is
 *  bottom-left, so y flips). */
export function coverCropFractions(s: PrintSettings = DEFAULT_PRINT_SETTINGS): {
  front: { x: number; y: number; w: number; h: number };
  spine: { x: number; y: number; w: number; h: number };
  back: { x: number; y: number; w: number; h: number };
} {
  const MM = 2.8346;
  // Vinyl size follows the case-spec formula, never a stale stored sheet.
  const ss = withDerivedVinyl(s);
  const layout = computeTemplateLayout(ss);
  const W = ss.sheetWmm * MM;
  const H = ss.sheetHmm * MM;
  const y = (H - layout.boardTop) / H;            // top of boards, canvas space
  const h = (layout.boardTop - layout.boardBottom) / H;
  return {
    back:  { x: layout.backBoardL / W,  y, w: (layout.backBoardR - layout.backBoardL) / W,   h },
    // Spine texture = the printed spine COLUMN (board + both hinge grooves) —
    // that's the visible strip on a closed book.
    spine: { x: layout.spineColL / W,   y, w: (layout.spineColR - layout.spineColL) / W,     h },
    front: { x: layout.frontBoardL / W, y, w: (layout.frontBoardR - layout.frontBoardL) / W, h },
  };
}

export function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// ─── Canvas plumbing ──────────────────────────────────────────────────────────

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      quality,
    );
  });
}

/** Tiny blurry placeholder as an inline data URI (lives in the manifest, so
 *  the flipbook paints every page instantly with zero extra requests). */
function makeLqip(source: HTMLCanvasElement): string {
  const w = LQIP_WIDTH_PX;
  const h = Math.max(1, Math.round((source.height / source.width) * w));
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d')!.drawImage(source, 0, 0, w, h);
  return c.toDataURL('image/jpeg', LQIP_JPEG_Q);
}

function cropToCanvas(
  source: HTMLCanvasElement,
  frac: { x: number; y: number; w: number; h: number },
): HTMLCanvasElement {
  const sx = Math.round(frac.x * source.width);
  const sy = Math.round(frac.y * source.height);
  const sw = Math.round(frac.w * source.width);
  const sh = Math.round(frac.h * source.height);
  const c = document.createElement('canvas');
  c.width = sw; c.height = sh;
  c.getContext('2d')!.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
  return c;
}

// ─── PDF rasterization (pdfjs, lazy-loaded) ───────────────────────────────────

export async function loadPdfjs() {
  const pdfjs = await import('pdfjs-dist');
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.js?url')).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  }
  return pdfjs;
}

/** Free a canvas's backing store NOW rather than waiting for GC.
 *
 *  (2026-08-07, order #D136.) Streaming the uploads took album 1 from 17 pages
 *  to 42 and let album 2 start at all, but both still died "Target closed".
 *  The remaining hog is the canvases themselves: one 1400px page canvas is
 *  ~5.5 MB of bitmap, created fresh per page and only reclaimed whenever GC
 *  feels like it — 42 of them is ~230 MB on top of a 182 MB PDF. Setting the
 *  dimensions to 0 releases the bitmap immediately, which is the one thing that
 *  makes peak memory genuinely O(1) per page instead of O(pages). */
function releaseCanvas(c: HTMLCanvasElement): void {
  c.width = 0;
  c.height = 0;
}

/** A neutral stand-in for a page that would not rasterize. Same aspect as the
 *  rest of the book so the flipbook's geometry is undisturbed. */
function placeholderCanvas(aspect: number): HTMLCanvasElement {
  const h = PAGE_LONG_EDGE_PX;
  const w = Math.max(1, Math.round(h * (aspect || 0.7071)));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { alpha: false })!;
  ctx.fillStyle = '#F5F0E8';
  ctx.fillRect(0, 0, w, h);
  return c;
}

export async function renderPdfPageToCanvas(
  page: import('pdfjs-dist').PDFPageProxy,
  longEdgePx: number,
): Promise<HTMLCanvasElement> {
  const base = page.getViewport({ scale: 1 });
  const scale = longEdgePx / Math.max(base.width, base.height);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d', { alpha: false })!;
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas;
}

/** The coupon pages a print interior declares in its Keywords ([] = none, or
 *  unreadable metadata — the raster then goes page for page, as before). */
async function pdfCouponPages(doc: { getMetadata: () => Promise<{ info?: unknown }> }): Promise<number[]> {
  try {
    const meta = await doc.getMetadata();
    return couponPagesFromKeywords((meta?.info as { Keywords?: string } | undefined)?.Keywords);
  } catch {
    return [];
  }
}

/** Rasterize cover.pdf (one A3+ wrap page) + interior.pdf (every page) into
 *  the upload-ready file list + manifest. Pages render sequentially to keep
 *  peak memory at ~one canvas on mid-range phones. */
export async function generatePreviewAssets(opts: {
  coverPdf: Blob;
  /** Whole-file interior. Fine for generated albums; for an operator-supplied
   *  book prefer `interiorUrl`, which streams instead of buffering. */
  interiorPdf?: Blob;
  /** STREAMING source, preferred when available. A manually-uploaded interior
   *  can be 300 MB; buffering that into the render browser killed it outright
   *  ("Protocol error: Target closed"). pdf.js fetches only the byte ranges it
   *  needs per page over the signed URL, so peak memory stays at ~one page. */
  interiorUrl?: string;
  printSettings?: PrintSettings;
  onProgress?: (p: PreviewProgress) => void;
  /** STREAMING SINK (2026-08-07, order #D136). When given, each JPEG is handed
   *  over the moment it is rasterized and is NOT retained — peak memory becomes
   *  one page instead of the whole album.
   *
   *  Streaming the INPUT (disableAutoFetch above) was only half the problem: the
   *  OUTPUT still accumulated every page. On #D136 — a Duo whose operator-
   *  supplied interiors are 182 MB and 111 MB — the browser rasterized all 96
   *  pages of album 1, held ~96 JPEGs plus their canvases in memory, then died
   *  17 files into the upload. Album 2 never started, and because the folder is
   *  wiped before writing, its previous images were already gone.
   *
   *  Omit it and the old buffering behaviour is unchanged. */
  onFile?: (f: PreviewAssetFile) => Promise<void>;
  /** Per-page fingerprints of what these PDFs were drawn from (quick redraws). */
  renderKeys?: PreviewRenderKeys | null;
  /** Version prefix of every file name (« p<stamp>- »). Each raster writes NEW
   *  files, so the book a reader has open keeps all of its pictures until the
   *  new manifest replaces it in one piece. '' = the historical names. */
  filePrefix?: string;
}): Promise<{ files: PreviewAssetFile[]; manifest: PreviewManifest }> {
  const { coverPdf, interiorPdf, interiorUrl, printSettings, onProgress, onFile } = opts;
  const prefix = opts.filePrefix ?? '';
  if (!interiorPdf && !interiorUrl) {
    throw new Error('generatePreviewAssets: interiorPdf or interiorUrl is required');
  }
  const progress = onProgress ?? (() => {});
  const pdfjs = await loadPdfjs();

  const files: PreviewAssetFile[] = [];
  /** Pages pdf.js could not rasterize — surfaced on the manifest so the operator
   *  knows which page to re-export instead of guessing. */
  const failedPages: number[] = [];
  /** Retain or stream — never both. Retaining is what blew the memory budget. */
  const emit = async (f: PreviewAssetFile) => {
    if (onFile) await onFile(f);
    else files.push(f);
  };
  const manifest: PreviewManifest = {
    version: 1,
    generated_at: new Date().toISOString(),
    page_count: 0,
    page_aspect: 0.7071,
    cover: {},
    pages: [],
  };

  // ── Cover wrap → three face crops ──
  progress({ current: 0, total: 1, message: 'Aperçu de la couverture…' });
  manifest.cover = await rasterizeCoverFaces(pdfjs, coverPdf, printSettings, (face) => `${prefix}cover-${face}.jpg`, emit);

  // ── Interior pages ──
  {
    // Range-stream when we have a URL. `disableAutoFetch` is what stops pdf.js
    // from quietly pulling the whole file in the background and undoing the
    // point of streaming.
    const doc = await (interiorUrl
      ? pdfjs.getDocument({
          url: interiorUrl,
          rangeChunkSize: 262144,
          disableAutoFetch: true,
          disableStream: false,
        }).promise
      : pdfjs.getDocument({ data: new Uint8Array(await interiorPdf!.arrayBuffer()) }).promise);
    try {
      const total = doc.numPages;
      manifest.page_count = total;
      // ⚠️ COUPON PAGES ARE NEVER SHOWN (plan 2026-10-28). A print interior
      // that carries the order's coupon leaf says so in its own Keywords
      // (couponLeaves.COUPON_PAGES_KEYWORD); each coupon page is pictured as
      // the page before the leaf — the outro, exactly as before coupons. A PDF
      // without the marker is rasterised page for page, as always.
      const couponPages = await pdfCouponPages(doc);
      // HEAP HEARTBEAT (#1144). The leading theory for the render browser dying
      // mid-album is memory, but nothing ever measured it, so the theory could
      // neither be confirmed nor dropped. Sampling every 5 pages costs nothing
      // and the worker now captures this console stream — if the trace shows
      // used climbing toward the limit right before a "Target closed", it IS
      // memory; if it dies flat at 200 MB of a 4 GB limit, it is not.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mem = (performance as any)?.memory;
      for (let n = 1; n <= total; n++) {
        progress({ current: n, total, message: `Aperçu page ${n}/${total}…` });
        if (mem && (n === 1 || n % 5 === 0 || n === total)) {
          const mb = (b: number) => Math.round(b / 1048576);
          console.log(`[preview] heap page ${n}/${total}: used=${mb(mem.usedJSHeapSize)}MB total=${mb(mem.totalJSHeapSize)}MB limit=${mb(mem.jsHeapSizeLimit)}MB`);
        }
        const file = `${prefix}page-${n}.jpg`;
        // ONE BAD PAGE MUST NOT COST THE WHOLE BOOK (2026-08-07, order #D136).
        //
        // Album 2 stopped at page 42 of 48 while album 1 — a BIGGER pdf (182 MB
        // vs 111 MB) with MORE pages — finished cleanly. That asymmetry does not
        // look like a memory ceiling; it looks like a specific page pdf.js
        // cannot render, which is entirely possible in an operator-supplied
        // export. Previously such a page threw, the album produced no manifest,
        // and the customer was left on the spinner with NOTHING — the folder
        // having already been wiped.
        //
        // A placeholder for the offending page is strictly better than no book:
        // the customer can still review and approve the other 47, and the
        // operator sees exactly which page needs re-exporting.
        let page: Awaited<ReturnType<typeof doc.getPage>> | null = null;
        let canvas: HTMLCanvasElement | null = null;
        try {
          page = await doc.getPage(previewSourcePage(n, couponPages));
          canvas = await renderPdfPageToCanvas(page, PAGE_LONG_EDGE_PX);
          if (n === 1) manifest.page_aspect = canvas.width / canvas.height;
          await emit({ name: file, blob: await canvasToJpegBlob(canvas, PAGE_JPEG_Q) });
          manifest.pages.push({ n, file, lqip: makeLqip(canvas) });
        } catch (err) {
          // Now actually visible: the worker captures this page's console
          // (previewRender.ts renderOneAlbum, #1144). Before that listener
          // existed this warning went nowhere.
          console.warn(`[preview] page ${n} failed to rasterize — using a placeholder`, err instanceof Error ? (err.stack ?? err.message) : String(err));
          failedPages.push(n);
          const ph = placeholderCanvas(manifest.page_aspect);
          await emit({ name: file, blob: await canvasToJpegBlob(ph, PAGE_JPEG_Q) });
          manifest.pages.push({ n, file, lqip: makeLqip(ph) });
          releaseCanvas(ph);
        } finally {
          // Release BOTH halves before the next page: pdf.js's page resources and
          // the canvas bitmap. Dropping only the first still leaks ~5.5 MB a page.
          if (canvas) releaseCanvas(canvas);
          try { page?.cleanup(); } catch { /* already gone */ }
        }
      }
    } finally {
      await doc.destroy();
    }
  }

  if (failedPages.length > 0) manifest.failed_pages = failedPages;
  if (opts.renderKeys) manifest.render_keys = opts.renderKeys;
  return { files, manifest };
}

/** Cover wrap → the three face JPEGs + their manifest entries. Shared by the
 *  print raster above and the quick preview (one crop geometry, never two). */
async function rasterizeCoverFaces(
  pdfjs: Awaited<ReturnType<typeof loadPdfjs>>,
  coverPdf: Blob,
  printSettings: PrintSettings | undefined,
  nameFor: (face: 'front' | 'spine' | 'back') => string,
  emit: (f: PreviewAssetFile) => Promise<void>,
): Promise<PreviewManifest['cover']> {
  const out: PreviewManifest['cover'] = {};
  const doc = await pdfjs.getDocument({ data: new Uint8Array(await coverPdf.arrayBuffer()) }).promise;
  try {
    const wrapPage = await doc.getPage(1);
    // Render the wrap large enough that the FRONT crop lands at the sharp
    // tier: front board height is the wrap's dominant face dimension.
    const s = withDerivedVinyl(printSettings ?? DEFAULT_PRINT_SETTINGS);
    const wrapLongEdge = Math.round(PAGE_LONG_EDGE_PX * (s.sheetWmm / s.boardHmm));
    const wrapCanvas = await renderPdfPageToCanvas(wrapPage, wrapLongEdge);
    const crops = coverCropFractions(s);
    for (const face of ['front', 'spine', 'back'] as const) {
      const c = cropToCanvas(wrapCanvas, crops[face]);
      const file = nameFor(face);
      await emit({ name: file, blob: await canvasToJpegBlob(c, PAGE_JPEG_Q) });
      out[face] = { file, aspect: c.width / c.height, lqip: makeLqip(c) };
      releaseCanvas(c);
    }
    // The A3+ wrap is the single biggest canvas in the run (~14 MB).
    releaseCanvas(wrapCanvas);
  } finally {
    await doc.destroy();
  }
  return out;
}

// ─── Quick preview (owner, 2026-10-18) ────────────────────────────────────────
//
// lib/quickPreview.ts redraws only the pages that changed. These two pieces are
// its raster + bookkeeping; the drawing itself is the print code (pdfGenerator).

/** Files the quick preview writes. Distinct from the print raster's names so a
 *  print raster running at the same moment can never interleave with them. */
export const quickPageFile = (n: number, tag = '') => `q${tag}-page-${n}.jpg`;
export const quickCoverFile = (face: 'front' | 'spine' | 'back', tag = '') => `q${tag}-cover-${face}.jpg`;

/** A short, sortable version tag for file names (base-36 milliseconds). Pure. */
export function fileVersionTag(nowMs: number): string {
  return Math.max(0, Math.floor(nowMs)).toString(36);
}

/**
 * Rasterize chosen pages of a quick-preview PDF. STRICT: a page that will not
 * draw throws (no placeholder) — the caller then leaves the preview to the
 * print rebuild instead of publishing a hole.
 *
 * `pages` maps a page of THIS pdf (`pdfPage`, 1-based) to the page number it
 * takes in the album's preview (`n`).
 */
export async function rasterizeQuickPages(opts: {
  interiorPdf?: Uint8Array | null;
  /** 'all' = every page of the PDF, numbered as it is (a whole-album redraw). */
  pages: Array<{ pdfPage: number; n: number }> | 'all';
  coverPdf?: Uint8Array | null;
  printSettings?: PrintSettings;
  emit: (f: PreviewAssetFile) => Promise<void>;
  /** Version tag in every file name (fileVersionTag) — never overwrite a live picture. */
  tag?: string;
  /** Called after the cover and after each page. */
  onStep?: (s: { cover: boolean; done: number; total: number }) => void;
}): Promise<{ pages: PreviewManifest['pages']; cover: PreviewManifest['cover'] | null; pageAspect: number | null; pageCount: number | null }> {
  const pdfjs = await loadPdfjs();
  const tag = opts.tag ?? '';
  let cover: PreviewManifest['cover'] | null = null;
  // The cover FIRST: it is the first thing anyone opening the book sees.
  if (opts.coverPdf) {
    cover = await rasterizeCoverFaces(pdfjs, new Blob([opts.coverPdf as BlobPart], { type: 'application/pdf' }),
      opts.printSettings, (face) => quickCoverFile(face, tag), opts.emit);
    opts.onStep?.({ cover: true, done: 0, total: opts.pages === 'all' ? 0 : opts.pages.length });
  }
  const pages: PreviewManifest['pages'] = [];
  let pageAspect: number | null = null;
  let pageCount: number | null = null;
  if (opts.interiorPdf && (opts.pages === 'all' || opts.pages.length > 0)) {
    const doc = await pdfjs.getDocument({ data: opts.interiorPdf.slice() }).promise;
    try {
      pageCount = doc.numPages;
      const wanted = opts.pages === 'all'
        ? Array.from({ length: doc.numPages }, (_, i) => ({ pdfPage: i + 1, n: i + 1 }))
        : opts.pages;
      // Coupon pages never pictured (plan 2026-10-28) — a quick interior has
      // none (generatePreviewPdfs strips them); the marker check is the lock.
      const couponPages = await pdfCouponPages(doc);
      for (const { pdfPage, n } of wanted) {
        if (pdfPage < 1 || pdfPage > doc.numPages) throw new Error(`quick preview: page ${pdfPage} not in a ${doc.numPages}-page PDF`);
        const page = await doc.getPage(previewSourcePage(pdfPage, couponPages));
        let canvas: HTMLCanvasElement | null = null;
        try {
          canvas = await renderPdfPageToCanvas(page, PAGE_LONG_EDGE_PX);
          if (pageAspect == null) pageAspect = canvas.width / canvas.height;
          const file = quickPageFile(n, tag);
          await opts.emit({ name: file, blob: await canvasToJpegBlob(canvas, PAGE_JPEG_Q) });
          pages.push({ n, file, lqip: makeLqip(canvas) });
          opts.onStep?.({ cover: !!cover, done: pages.length, total: wanted.length });
        } finally {
          if (canvas) releaseCanvas(canvas);
          try { page.cleanup(); } catch { /* already gone */ }
        }
      }
    } finally {
      await doc.destroy();
    }
  }
  return { pages, cover, pageAspect, pageCount };
}

/**
 * The manifest after a quick redraw of SOME pages. Pure.
 *
 * Every page and cover face not redrawn keeps its file (print or an earlier
 * quick pass); redrawn ones point at the new files; a redrawn page is no longer
 * « failed ». The date is the new one — it is what tells the open preview page
 * and the « version précédente » banner that the album is current.
 */
export function mergeQuickManifest(base: PreviewManifest, update: {
  pages: PreviewManifest['pages'];
  cover?: PreviewManifest['cover'] | null;
  renderKeys: PreviewRenderKeys;
  generatedAt: string;
}): PreviewManifest {
  const byN = new Map(base.pages.map((p) => [p.n, p]));
  for (const p of update.pages) byN.set(p.n, p);
  const redrawn = new Set(update.pages.map((p) => p.n));
  const failed = (base.failed_pages ?? []).filter((n) => !redrawn.has(n));
  const merged: PreviewManifest = {
    ...base,
    generated_at: update.generatedAt,
    pages: [...byN.values()].sort((a, b) => a.n - b.n),
    cover: { ...base.cover, ...(update.cover ?? {}) },
    render_keys: update.renderKeys,
    quick: true,
  };
  if (failed.length > 0) merged.failed_pages = failed; else delete merged.failed_pages;
  return merged;
}

// ─── Upload ───────────────────────────────────────────────────────────────────

export async function uploadPreviewAssets(opts: {
  shopifyOrderId: string;
  albumIndex: number;
  files: PreviewAssetFile[];
  manifest: PreviewManifest;
  onProgress?: (p: PreviewProgress) => void;
}): Promise<void> {
  const { shopifyOrderId, albumIndex, files, manifest, onProgress } = opts;
  const progress = onProgress ?? (() => {});

  // Wipe first so a shorter regen never leaves stale trailing pages.
  await previewAssetsApi.reset(shopifyOrderId, albumIndex);

  let done = 0;
  const total = files.length + 1; // + manifest
  for (const group of chunk(files, UPLOAD_CHUNK)) {
    const { urls } = await previewAssetsApi.uploadUrls(
      shopifyOrderId, albumIndex, group.map((f) => f.name),
    );
    const urlByName = new Map(urls.map((u) => [u.file, u.upload_url]));
    // Modest parallelism — these are ~200 KB JPEGs, not the 40 MB PDFs.
    for (const batch of chunk(group, 6)) {
      await Promise.all(batch.map(async (f) => {
        const url = urlByName.get(f.name);
        if (!url) throw new Error(`No upload URL for ${f.name}`);
        await uploadToSignedUrl(url, new File([f.blob], f.name, { type: 'image/jpeg' }), 'image/jpeg');
      }));
      done += batch.length;
      progress({ current: done, total, message: `Téléversement de l'aperçu (${done}/${total})…` });
    }
  }

  // Manifest LAST — its presence is the "previews ready" marker the public
  // route keys on, so a half-finished upload is simply invisible.
  const { urls } = await previewAssetsApi.uploadUrls(shopifyOrderId, albumIndex, ['manifest.json']);
  const manifestUrl = urls[0]?.upload_url;
  if (!manifestUrl) throw new Error('No upload URL for manifest.json');
  await uploadToSignedUrl(
    manifestUrl,
    new File([JSON.stringify(manifest)], 'manifest.json', { type: 'application/json' }),
    'application/json',
  );
  progress({ current: total, total, message: 'Aperçu prêt ✓' });
}

/** One-call entry used by BOTH the confirm flow (customer browser, PDFs just
 *  generated) and the admin backfill (admin browser, PDFs fetched from their
 *  stored URLs). */
export async function generateAndUploadPreviewAssets(opts: {
  shopifyOrderId: string;
  albumIndex: number;
  coverPdf: Blob;
  interiorPdf?: Blob;
  /** Preferred over interiorPdf — streams instead of buffering. */
  interiorUrl?: string;
  printSettings?: PrintSettings;
  onProgress?: (p: PreviewProgress) => void;
  /** Per-page fingerprints of what these PDFs were drawn from (quick redraws). */
  renderKeys?: PreviewRenderKeys | null;
}): Promise<void> {
  const { shopifyOrderId, albumIndex, onProgress } = opts;
  const progress = onProgress ?? (() => {});

  // STREAMING (2026-08-07, #D136). Rasterize → upload → release, a few pages at
  // a time, instead of building the whole album in memory and uploading at the
  // end. Peak memory is now FLUSH_EVERY JPEGs rather than the entire album, so a
  // 182 MB operator-supplied interior no longer kills the render browser.
  //
  // The two invariants of the old path are kept exactly:
  //   • the manifest is written LAST, because its presence is the "previews
  //     ready" marker — a half-finished upload must stay invisible;
  //   • a shorter regen leaves no stale pages: the files no book names any more
  //     are pruned AFTER the new manifest is written (2026-10-21).
  //
  // ⚠️ NO MORE WIPE BEFORE WRITING (2026-10-21). The folder used to be emptied
  // first — manifest included — so for the 20–40 s of every print raster the
  // client's open book had no manifest (« en préparation ») and its pictures
  // 404'd, and a quick book's pictures were deleted under it (edge logs,
  // #1256 on 10-20 21:42:10: manifest GET 400 mid-raster). Every raster now
  // writes NEW names (« p<stamp>-page-N.jpg ») and the manifest swaps the whole
  // book in one write: the old book stays complete until the new one is.
  const FLUSH_EVERY = 6;
  let pending: PreviewAssetFile[] = [];
  let uploaded = 0;
  const filePrefix = `p${fileVersionTag(Date.now())}-`;

  const flush = async () => {
    if (pending.length === 0) return;
    const group = pending;
    pending = [];   // drop our references before the awaits so they can be freed
    const { urls } = await previewAssetsApi.uploadUrls(
      shopifyOrderId, albumIndex, group.map((f) => f.name),
    );
    const urlByName = new Map(urls.map((u) => [u.file, u.upload_url]));
    await Promise.all(group.map(async (f) => {
      const url = urlByName.get(f.name);
      if (!url) throw new Error(`No upload URL for ${f.name}`);
      await uploadToSignedUrl(url, new File([f.blob], f.name, { type: 'image/jpeg' }), 'image/jpeg');
    }));
    uploaded += group.length;
    progress({ current: uploaded, total: uploaded, message: `Aperçu téléversé (${uploaded})…` });
  };

  const { manifest } = await generatePreviewAssets({
    ...opts,
    filePrefix,
    onFile: async (f) => {
      pending.push(f);
      if (pending.length >= FLUSH_EVERY) await flush();
    },
  });
  await flush();

  const { urls } = await previewAssetsApi.uploadUrls(shopifyOrderId, albumIndex, ['manifest.json']);
  const manifestUrl = urls[0]?.upload_url;
  if (!manifestUrl) throw new Error('No upload URL for manifest.json');
  await uploadToSignedUrl(
    manifestUrl,
    new File([JSON.stringify(manifest)], 'manifest.json', { type: 'application/json' }),
    'application/json',
  );
  // Files of older books go now (the worker keeps anything < 15 min old and
  // everything this manifest names). Best effort: an old worker without the
  // route just leaves them, which costs storage, never a picture.
  try { await previewAssetsApi.prune(shopifyOrderId, albumIndex); } catch { /* ignore */ }
  progress({ current: uploaded + 1, total: uploaded + 1, message: 'Aperçu prêt ✓' });
}
